import { debounce } from 'lodash'
import dayjs from 'lib/dayjs'
import type { Dayjs } from 'dayjs'
import { SetStateAction, useCallback } from 'react'

export const usePushNotification = ({
	showToast,
	setPublisDate,
	setDataCurrency,
	endDate,
	endRadio,
	setEndDate,
	dataCurrency,
	contentScheduleStartDate
}: {
	showToast: (
		title: string,
		subtitle: string,
		type: 'success' | 'error' | 'warning'
	) => void
	setPublisDate: React.Dispatch<React.SetStateAction<string | Dayjs>>
	setDataCurrency: React.Dispatch<React.SetStateAction<string | Dayjs>>
	endDate: string | Dayjs
	endRadio: {
		id: string
		title: string
		value: string
	}
	setEndDate: React.Dispatch<React.SetStateAction<string | Dayjs>>
	dataCurrency: string | Dayjs
	contentScheduleStartDate?: string
}): {
	isValidFutureDate: (date: string | Dayjs) => boolean
	handleChangeSchedule: (
		valueReleaseDate: SetStateAction<string | Dayjs>
	) => void
	isValidEndDateVsDataCurrency: (
		startRecurrenceDate: string | Dayjs,
		endRecurrenceDate: string | Dayjs
	) => boolean
	handleChangeDataCurrency: (
		valueCurrency: SetStateAction<string | Dayjs>
	) => void
	handleChangeEndDate: (valueEndDate: SetStateAction<string | Dayjs>) => void
} => {
	const showToastDebounced = useCallback(
		debounce(
			(
				title: string,
				message: string,
				type: 'error' | 'success' | 'warning'
			) => {
				showToast(title, message, type)
			},
			4000
		),
		[]
	)

	const isValidFutureDate = (date: string | Dayjs): boolean => {
		if (!date) return false
		const now = dayjs()
		const dateToValidate = dayjs(date as string)
		return dateToValidate.isAfter(now)
	}

	const handleChangeSchedule = (
		valueReleaseDate: SetStateAction<string | Dayjs>
	): void => {
		const now = dayjs()
		const valueDate = valueReleaseDate as Dayjs
		const contentStart = contentScheduleStartDate
			? dayjs.utc(contentScheduleStartDate)
			: null

		const isBeforeNow =
			valueDate &&
			(dayjs(valueDate).isBefore(now) || dayjs(valueDate).isSame(now))
		const isBeforeContentSchedule =
			contentStart && valueDate && dayjs(valueDate).isBefore(contentStart)

		if (isBeforeNow) {
			showToastDebounced(
				'Error',
				'Oops! The release date cannot be before or equal to the current time and date.',
				'error'
			)
		} else if (isBeforeContentSchedule) {
			showToastDebounced(
				'Error',
				'Oops! The release date cannot be before the content schedule start date and time.',
				'error'
			)
		} else {
			showToastDebounced.cancel()
		}

		setPublisDate(valueDate)
	}

	const isValidEndDateVsDataCurrency = (
		startRecurrenceDate: string | Dayjs,
		endRecurrenceDate: string | Dayjs
	): boolean => {
		if (!startRecurrenceDate || !endRecurrenceDate) return false
		const start = dayjs(startRecurrenceDate as string)
		const end = dayjs(endRecurrenceDate as string)
		return end.isAfter(start)
	}

	const handleChangeDataCurrency = (
		valueCurrency: SetStateAction<string | Dayjs>
	): void => {
		const now = dayjs()
		const valueDate = valueCurrency as Dayjs
		const contentStart = contentScheduleStartDate
			? dayjs.utc(contentScheduleStartDate)
			: null

		const isBeforeNow =
			valueDate &&
			(dayjs(valueDate).isBefore(now) || dayjs(valueDate).isSame(now))
		const isBeforeContentSchedule =
			contentStart && valueDate && dayjs(valueDate).isBefore(contentStart)

		if (isBeforeNow) {
			showToastDebounced(
				'Error',
				'Oops! The recurrence start date cannot be before or equal to the current time and date.',
				'error'
			)
		} else if (isBeforeContentSchedule) {
			showToastDebounced(
				'Error',
				'Oops! The recurrence start date cannot be before the content schedule start date and time.',
				'error'
			)
		} else {
			showToastDebounced.cancel()
		}

		setDataCurrency(valueDate)

		if (endDate && valueDate && endRadio.value === 'specific_day') {
			const isEndDateInvalid =
				dayjs(endDate as string).isBefore(dayjs(valueDate)) ||
				dayjs(endDate as string).isSame(dayjs(valueDate))

			if (isEndDateInvalid) {
				showToastDebounced(
					'Error',
					'Oops! The end date cannot be before or equal to the recurrence start date. Please select a later date.',
					'error'
				)
			}
		}
	}

	const handleChangeEndDate = (
		valueEndDate: SetStateAction<string | Dayjs>
	): void => {
		const now = dayjs()
		const valueDate = valueEndDate as Dayjs

		const isInvalidTime =
			valueDate &&
			(dayjs(valueDate).isBefore(now) || dayjs(valueDate).isSame(now))

		const isInvalidOrder =
			valueDate &&
			dataCurrency &&
			(dayjs(valueDate).isBefore(dayjs(dataCurrency as string)) ||
				dayjs(valueDate).isSame(dayjs(dataCurrency as string)))

		if (isInvalidTime) {
			showToastDebounced(
				'Error',
				'Oops! The end date cannot be before or equal to the current time and date.',
				'error'
			)
		} else if (isInvalidOrder) {
			showToastDebounced(
				'Error',
				'Oops! The end date cannot be before or equal to the recurrence start date. Please select a later date.',
				'error'
			)
		} else {
			showToastDebounced.cancel()
		}

		setEndDate(valueDate)
	}

	return {
		isValidFutureDate,
		handleChangeSchedule,
		isValidEndDateVsDataCurrency,
		handleChangeDataCurrency,
		handleChangeEndDate
	}
}
